// ABOUTME: Layout route for /dashboard/* pages
// ABOUTME: Shared header, filter bar, admin guard, data-fetching, and DashboardContext provider

import { DashboardContext } from "@/components/dashboard/DashboardContext";
import { fetchAllRows } from "@/components/dashboard/fetch-all-rows";
import {
  type DashboardData,
  EXCLUDED_USER_IDS,
  TIME_RANGE_DAYS,
  type TimeRange,
  fillMissingDates,
  getFriendlyTimezoneLabel,
  getMessageShift,
  groupLineOptionsByLocation,
  shiftAwareDate,
} from "@/components/dashboard/utils";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase, useAuth } from "@/contexts/AuthContext";
import {
  DEFAULT_CUSTOMER_TIMEZONE,
  DEFAULT_SHIFTS,
} from "@/contexts/customer-context";
import type { ShiftDefinition } from "@/contexts/customer-context";
import { useCustomer } from "@/contexts/useCustomer";
import { useQuery } from "@tanstack/react-query";
import { Outlet, createFileRoute, useLocation } from "@tanstack/react-router";
import { jwtDecode } from "jwt-decode";
import { useState } from "react";

interface JWTPayload {
  user_role?: string;
  [key: string]: unknown;
}

interface ShiftTemplateRow {
  key: string;
  name: string;
  start_minutes: number;
  end_minutes: number;
}

interface LineRow {
  id: string;
  name: string;
  location_id: string | null;
  locations: { name: string | null } | Array<{ name: string | null }> | null;
}

const getLineLocationName = (line: LineRow): string | null => {
  const location = Array.isArray(line.locations)
    ? line.locations[0]
    : line.locations;
  return location?.name ?? null;
};

const toUniqueShiftDefinitions = (
  templates: ShiftTemplateRow[] | null,
): ShiftDefinition[] => {
  const shiftsByKey = new Map<string, ShiftDefinition>();

  for (const template of templates ?? []) {
    if (shiftsByKey.has(template.key)) continue;

    shiftsByKey.set(template.key, {
      key: template.key,
      name: template.name,
      startMinutes: template.start_minutes,
      endMinutes: template.end_minutes,
    });
  }

  return Array.from(shiftsByKey.values());
};

export const Route = createFileRoute("/_authenticated/dashboard")({
  component: DashboardLayout,
});

function DashboardLayout() {
  const { user, session } = useAuth();
  const { selectedCustomerId, selectedCustomer } = useCustomer();
  const userId = user?.id ?? null;
  const customerTimezone =
    selectedCustomer?.timezone ?? DEFAULT_CUSTOMER_TIMEZONE;
  const customerTimezoneLabel = getFriendlyTimezoneLabel(customerTimezone);

  const pathname = useLocation({ select: (l) => l.pathname });
  const isRecentPage = pathname.startsWith("/dashboard/recent");
  const isConversationPage = pathname.startsWith("/dashboard/conversation");
  const isTriagePage = pathname.startsWith("/dashboard/triage");
  const isUsagePage = !isRecentPage && !isConversationPage && !isTriagePage;
  const pageTitle = isTriagePage
    ? "Feedback"
    : isRecentPage
      ? "Recent Queries"
      : isConversationPage
        ? "Conversation"
        : "Usage";
  const pageSubtitle = customerTimezoneLabel;
  const userRole = session?.access_token
    ? jwtDecode<JWTPayload>(session.access_token).user_role
    : null;
  const isAdmin = userRole === "admin" || userRole === "superadmin";
  const requiresAdmin = true;

  // Filter state
  const [lineId, setLineId] = useState("all");
  const [equipmentId, setEquipmentId] = useState("all");
  const [shiftId, setShiftId] = useState("all");
  const [timeRange, setTimeRange] = useState<TimeRange>("7d");

  const {
    data: dashboardData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "dashboard",
      selectedCustomerId,
      customerTimezone,
      lineId,
      equipmentId,
      shiftId,
      timeRange,
    ],
    queryFn: async (): Promise<DashboardData> => {
      const customerId = selectedCustomerId as string;

      const today = new Date();
      const days = TIME_RANGE_DAYS[timeRange];
      let startDate!: Date;
      let startDateISO: string | null = null;
      if (days != null) {
        startDate = new Date();
        startDate.setDate(startDate.getDate() - days);
        startDateISO = startDate.toISOString();
      }

      const [linesData, equipmentData] = await Promise.all([
        fetchAllRows(
          supabase
            .from("lines")
            .select("id, name, location_id, locations:location_id(name)")
            .eq("customer_id", customerId)
            .order("name")
            .order("id"),
        ),
        fetchAllRows(
          supabase
            .from("equipment")
            .select("id, name, line_id")
            .eq("customer_id", customerId)
            .order("name")
            .order("id"),
        ),
      ]);

      const lines = ((linesData || []) as LineRow[]).map((l) => ({
        id: l.id,
        name: l.name,
        locationId: l.location_id,
        locationName: getLineLocationName(l),
      }));
      const equipment = (equipmentData || []).map((e) => ({
        id: e.id,
        name: e.name,
        line_id: e.line_id,
      }));

      let equipmentMessageIds: Set<string> | null = null;
      if (equipmentId !== "all") {
        const meData = await fetchAllRows(
          supabase
            .from("message_equipment")
            .select("message_id, equipment!inner(customer_id)")
            .eq("equipment.customer_id", customerId)
            .eq("equipment_id", equipmentId)
            .order("message_id"),
        );
        equipmentMessageIds = new Set(
          (meData || []).map((me) => me.message_id),
        );
      }

      let convQuery = supabase
        .from("conversations")
        .select("id, created_at, user_id")
        .eq("customer_id", customerId)
        .eq("archived", false)
        .not("user_id", "in", `(${EXCLUDED_USER_IDS.join(",")})`);
      if (startDateISO) convQuery = convQuery.gte("created_at", startDateISO);
      const conversations = await fetchAllRows(
        convQuery.order("created_at", { ascending: true }).order("id"),
      );

      // For "all time", derive start date from the earliest conversation
      if (!startDateISO) {
        startDate =
          conversations && conversations.length > 0
            ? new Date(conversations[0].created_at)
            : new Date();
      }

      let shiftTemplatesQuery = supabase
        .from("line_shift_templates")
        .select(
          "key, name, start_minutes, end_minutes, line_id, lines!inner(customer_id)",
        )
        .eq("lines.customer_id", customerId)
        .eq("is_active", true)
        .order("sort_order")
        .order("id");

      if (lineId !== "all") {
        shiftTemplatesQuery = shiftTemplatesQuery.eq("line_id", lineId);
      }

      const shiftTemplates = await fetchAllRows(shiftTemplatesQuery);
      const templateShifts = toUniqueShiftDefinitions(shiftTemplates);
      const shifts: ShiftDefinition[] =
        templateShifts.length > 0 ? templateShifts : DEFAULT_SHIFTS;

      let messagesQuery = supabase
        .from("conversation_messages")
        .select(
          "id, created_at, conversation_id, sentiment, line_id, line_shift_id, conversations!inner(user_id, customer_id)",
        )
        .eq("role", "user")
        .eq("conversations.customer_id", customerId)
        .not("conversations.user_id", "in", `(${EXCLUDED_USER_IDS.join(",")})`)
        .order("created_at", { ascending: true })
        .order("id");
      if (startDateISO)
        messagesQuery = messagesQuery.gte("created_at", startDateISO);

      if (lineId !== "all") {
        messagesQuery = messagesQuery.eq("line_id", lineId);
      }

      const messages = await fetchAllRows(messagesQuery);

      let filteredMessages = messages;

      if (equipmentMessageIds) {
        filteredMessages = filteredMessages.filter((msg) =>
          equipmentMessageIds.has(msg.id),
        );
      }

      if (shiftId !== "all") {
        filteredMessages = filteredMessages.filter((msg) => {
          const matched = getMessageShift(
            msg.created_at,
            customerTimezone,
            shifts,
          );
          return matched?.key === shiftId;
        });
      }

      const matchingConversationIds = new Set(
        filteredMessages.map((m) => m.conversation_id),
      );

      const hasFilters =
        lineId !== "all" || equipmentId !== "all" || shiftId !== "all";
      const filteredConversations = hasFilters
        ? conversations.filter((c) => matchingConversationIds.has(c.id))
        : conversations;

      const groupedConvData = filteredConversations.reduce(
        (acc: Record<string, number>, conv) => {
          const date = shiftAwareDate(
            conv.created_at,
            customerTimezone,
            shifts,
          );
          acc[date] = (acc[date] || 0) + 1;
          return acc;
        },
        {},
      );

      const filledConvData = fillMissingDates(
        groupedConvData,
        0,
        startDate,
        today,
        customerTimezone,
      );

      const conversationData = Object.entries(filledConvData).map(
        ([date, count]) => ({
          date,
          count,
        }),
      );

      const groupedMessageData = filteredMessages.reduce(
        (acc: Record<string, number>, msg) => {
          const date = shiftAwareDate(msg.created_at, customerTimezone, shifts);
          acc[date] = (acc[date] || 0) + 1;
          return acc;
        },
        {},
      );

      const filledMessageData = fillMissingDates(
        groupedMessageData,
        0,
        startDate,
        today,
        customerTimezone,
      );

      const messageData = Object.entries(filledMessageData).map(
        ([date, count]) => ({
          date,
          count,
        }),
      );

      const emptyBucket: Record<string, number> = {};
      for (const s of shifts) emptyBucket[s.key] = 0;

      const groupedShiftData = filteredMessages.reduce(
        (acc: Record<string, Record<string, number>>, msg) => {
          const matchedShift = getMessageShift(
            msg.created_at,
            customerTimezone,
            shifts,
          );
          if (!matchedShift) return acc;

          const date = shiftAwareDate(msg.created_at, customerTimezone, shifts);
          if (!acc[date]) acc[date] = { ...emptyBucket };
          acc[date][matchedShift.key] += 1;

          return acc;
        },
        {},
      );

      const filledShiftData = fillMissingDates(
        groupedShiftData,
        emptyBucket,
        startDate,
        today,
        customerTimezone,
      );

      const shiftData = Object.entries(filledShiftData).map(
        ([date, counts]) => ({
          date,
          ...counts,
        }),
      );

      let assistantQuery = supabase
        .from("conversation_messages")
        .select(
          "id, created_at, sentiment, conversation_id, line_id, conversations!inner(user_id, customer_id)",
        )
        .eq("role", "assistant")
        .eq("conversations.customer_id", customerId)
        .not("conversations.user_id", "in", `(${EXCLUDED_USER_IDS.join(",")})`)
        .order("created_at", { ascending: true })
        .order("id");
      if (startDateISO)
        assistantQuery = assistantQuery.gte("created_at", startDateISO);

      if (lineId !== "all") {
        assistantQuery = assistantQuery.eq("line_id", lineId);
      }

      const assistantMessages = await fetchAllRows(assistantQuery);

      let filteredAssistantMessages = assistantMessages;

      if (equipmentMessageIds) {
        filteredAssistantMessages = filteredAssistantMessages.filter((msg) =>
          equipmentMessageIds.has(msg.id),
        );
      }

      if (shiftId !== "all") {
        filteredAssistantMessages = filteredAssistantMessages.filter((msg) => {
          const matched = getMessageShift(
            msg.created_at,
            customerTimezone,
            shifts,
          );
          return matched?.key === shiftId;
        });
      }

      if (hasFilters) {
        filteredAssistantMessages = filteredAssistantMessages.filter((msg) =>
          matchingConversationIds.has(msg.conversation_id),
        );
      }

      const groupedSentimentData = filteredAssistantMessages.reduce(
        (
          acc: Record<string, { thumbs_up: number; thumbs_down: number }>,
          msg,
        ) => {
          const date = shiftAwareDate(msg.created_at, customerTimezone, shifts);

          if (!acc[date]) {
            acc[date] = { thumbs_up: 0, thumbs_down: 0 };
          }

          if (msg.sentiment === 1) {
            acc[date].thumbs_up += 1;
          } else if (msg.sentiment === -1) {
            acc[date].thumbs_down += 1;
          }

          return acc;
        },
        {},
      );

      const filledSentimentData = fillMissingDates(
        groupedSentimentData,
        { thumbs_up: 0, thumbs_down: 0 },
        startDate,
        today,
        customerTimezone,
      );

      const sentimentData = Object.entries(filledSentimentData).map(
        ([date, sentiment]) => ({
          date,
          thumbs_up: sentiment.thumbs_up,
          thumbs_down: sentiment.thumbs_down,
        }),
      );

      let recentConvsQuery = supabase
        .from("conversations")
        .select("id, title, created_at, user_id")
        .eq("customer_id", customerId)
        .eq("archived", false)
        .not("user_id", "in", `(${EXCLUDED_USER_IDS.join(",")})`)
        .order("created_at", { ascending: false });

      if (hasFilters) {
        const ids = Array.from(matchingConversationIds);
        if (ids.length > 0) {
          recentConvsQuery = recentConvsQuery.in("id", ids);
        } else {
          return {
            conversationData,
            messageData,
            shiftData,
            sentimentData,
            recentMessages: [],
            shifts,
            lines,
            equipment,
            userNames: {},
          };
        }
      }

      const { data: recentConvs, error: recentError } =
        await recentConvsQuery.limit(10);

      if (recentError) throw recentError;

      const userIds = [...new Set((recentConvs || []).map((c) => c.user_id))];
      const userNames: Record<string, string> = {};
      if (userIds.length > 0) {
        const { data: usersData } = await supabase
          .from("users")
          .select("id, name, email")
          .in("id", userIds);
        for (const u of usersData || []) {
          userNames[u.id] = u.name || u.email || "Unknown";
        }
      }

      return {
        conversationData,
        messageData,
        shiftData,
        sentimentData,
        recentMessages: recentConvs || [],
        shifts,
        lines,
        equipment,
        userNames,
      };
    },
    enabled: requiresAdmin && !!userId && !!selectedCustomerId,
  });

  const shifts = dashboardData?.shifts ?? DEFAULT_SHIFTS;
  const lines = dashboardData?.lines ?? [];
  const lineGroups = groupLineOptionsByLocation(lines);
  const allEquipment = dashboardData?.equipment ?? [];
  const userNames = dashboardData?.userNames ?? {};

  const filteredEquipment =
    lineId === "all"
      ? allEquipment
      : allEquipment.filter((e) => e.line_id === lineId);

  if (requiresAdmin && !isAdmin) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4">
        <h1 className="text-2xl font-bold">Unauthorized</h1>
        <p className="text-muted-foreground">
          You do not have permission to view the admin dashboard.
        </p>
        <p className="text-sm text-muted-foreground">
          Admin access is required to view all conversations and analytics.
        </p>
      </div>
    );
  }

  if (requiresAdmin && isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-muted-foreground">Loading dashboard...</p>
      </div>
    );
  }

  if (requiresAdmin && error) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-destructive">Failed to load dashboard data</p>
      </div>
    );
  }

  return (
    <DashboardContext.Provider
      value={{
        dashboardData,
        isLoading,
        error,
        lines,
        shifts,
        filteredEquipment,
        userNames,
        customerTimezoneLabel,
        lineId,
        setLineId: (val: string) => {
          setLineId(val);
          setEquipmentId("all");
        },
        equipmentId,
        setEquipmentId,
        shiftId,
        setShiftId,
        timeRange,
        setTimeRange,
      }}
    >
      <div className="flex flex-1 flex-col overflow-auto">
        {/* Header — hidden on conversation viewer */}
        {!isConversationPage && (
          <div className="border-b border-border">
            <div className="flex items-center gap-4 p-4 pl-14 md:pl-4">
              <h1 className="text-xl font-semibold h-10 flex items-center">
                {pageTitle}
              </h1>
            </div>
            <div className="px-4 pb-3 text-sm text-muted-foreground">
              {pageSubtitle}
            </div>
          </div>
        )}

        {/* Filter Bar — shown on usage page only */}
        {isUsagePage && (
          <div className="border-b border-border px-4 py-3 flex flex-wrap gap-3 items-center">
            <Select
              value={lineId}
              onValueChange={(val) => {
                setLineId(val);
                setEquipmentId("all");
              }}
            >
              <SelectTrigger className="w-[240px]">
                <SelectValue placeholder="All Lines" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Lines</SelectItem>
                {lineGroups.map((group) => (
                  <SelectGroup key={group.locationId ?? "unassigned"}>
                    <SelectLabel className="mx-1 my-1 rounded-sm bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
                      {group.locationName}
                    </SelectLabel>
                    {group.lines.map((line) => (
                      <SelectItem key={line.id} value={line.id}>
                        {line.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>

            <Select value={equipmentId} onValueChange={setEquipmentId}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Equipment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Equipment</SelectItem>
                {filteredEquipment.map((eq) => (
                  <SelectItem key={eq.id} value={eq.id}>
                    {eq.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={shiftId} onValueChange={setShiftId}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="All Shifts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Shifts</SelectItem>
                {shifts.map((shift) => (
                  <SelectItem key={shift.key} value={shift.key}>
                    {shift.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={timeRange}
              onValueChange={(val) => setTimeRange(val as TimeRange)}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="14d">Last 14 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="all">All time</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <Outlet />
      </div>
    </DashboardContext.Provider>
  );
}
